/**
 * Pure decision logic for the "Value Moment" upsell.
 *
 * Fired the moment a FREE user's generation completes ("you made something good")
 * — the highest-intent, lowest-annoyance upgrade prompt. This module owns the
 * rules (who / how often / the offer-window math); the component owns the React
 * glue + the cookie + the `trackEvent` call. Extracted so the guards can be
 * unit-tested without React, a cookie jar, or a live analytics client.
 *
 * The two hard guards (both load-bearing):
 *   1. FREE users only. A paying subscriber must never see an upsell — this is
 *      the exact class of bug the PR #91 adversarial pass caught (an existing
 *      subscriber getting the checkout-abandon modal).
 *   2. At most once per rolling 48h window per user. The window is anchored to
 *      the first show (stored in a cookie) so the countdown is stable and the
 *      user isn't re-prompted on every single generation.
 */

/** 48h personal offer window, anchored to the first show. */
export const VALUE_MOMENT_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Cookie key holding the ms-epoch anchor of the user's first value-moment show. */
export const VALUE_MOMENT_COOKIE = "valueMomentUpsellShownAt";

/** The typed detail carried on the `musicfy:value-moment` window event. */
export interface ValueMomentDetail {
  /** The voice/model they just used — e.g. "Spongebob Squarepants". */
  topVoice?: string;
  /** The track they just made — e.g. "My Spongebob cover". */
  trackName?: string;
}

/** The window CustomEvent name the generation flow dispatches on completion. */
export const VALUE_MOMENT_EVENT = "musicfy:value-moment";

/**
 * Fire the value-moment signal from a generation-complete handler. Additive,
 * SSR-safe (no-ops without `window`), and carries no gating of its own — the
 * listener (ValueMomentUpsell) owns the free / once-per-48h decision. Callers
 * should still only fire for free users (cheap early-out at the call site).
 */
export function dispatchValueMoment(detail: ValueMomentDetail = {}): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<ValueMomentDetail>(VALUE_MOMENT_EVENT, { detail })
  );
}

/**
 * Affirmatively "the user is on the free plan." Load-bearing payer-safety gate.
 *
 * The subscription context is `null` while the plan query is loading AND if it
 * errors (its onError only logs). Treating `null` as "free" is the exact bug the
 * PR #91 adversarial pass exists to prevent — a payer whose plan fetch blips
 * would be seen as free and shown an upsell. So we return true ONLY when the
 * plan is known and equals "free"; unknown/null/loading/paid → false (suppress).
 *
 * `"free"` must equal Prisma's `SubscriptionPlans.free` — kept as a literal so
 * this stays a pure, dependency-free, unit-testable function.
 */
export function isFreePlan(plan: string | null | undefined): boolean {
  return plan === "free";
}

export interface ValueMomentGateInput {
  /** Is the current user on the free plan (no active paid subscription). */
  isFree: boolean;
  /** ms-epoch of the user's first-ever value-moment show, or null if never. */
  lastShownAt: number | null;
  /** Current time in ms-epoch. */
  now: number;
  /** Override the window (tests). Defaults to VALUE_MOMENT_WINDOW_MS. */
  windowMs?: number;
}

/**
 * Should we show the value-moment upsell right now?
 *
 * - Never to a payer (guard 1).
 * - Never shown before  → show (and the caller anchors the cookie).
 * - Shown within the window → suppress (already offered this window).
 * - Window elapsed → show again.
 */
export function shouldShowValueMoment({
  isFree,
  lastShownAt,
  now,
  windowMs = VALUE_MOMENT_WINDOW_MS,
}: ValueMomentGateInput): boolean {
  if (!isFree) return false;
  if (lastShownAt == null) return true;
  return now >= lastShownAt + windowMs;
}

/**
 * The countdown target: `windowMs` after the anchor (the first show). Stable
 * across re-renders because the anchor is read from the cookie, not `now`.
 */
export function computeOfferEnd(
  anchor: number,
  windowMs = VALUE_MOMENT_WINDOW_MS
): number {
  return anchor + windowMs;
}

/** Parse a cookie value to a ms-epoch anchor, or null if absent/garbage. */
export function parseAnchor(raw: string | undefined | null): number | null {
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Split a remaining-ms duration into padded D/H/M/S display parts. */
export function splitRemaining(remainingMs: number): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
} {
  const clamped = Math.max(0, remainingMs);
  const ONE_DAY = 24 * 60 * 60 * 1000;
  return {
    days: Math.floor(clamped / ONE_DAY),
    hours: Math.floor((clamped % ONE_DAY) / (60 * 60 * 1000)),
    minutes: Math.floor((clamped % (60 * 60 * 1000)) / (60 * 1000)),
    seconds: Math.floor((clamped % (60 * 1000)) / 1000),
  };
}
